// Real Firebase Messages Service

import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  writeBatch,
  increment,
} from 'firebase/firestore';
import { db } from './firebase';

export type AudienceType = 'all' | 'top-voters' | 'no-requests' | 'specific';

export interface Message {
  id: string;
  eventId: string;
  content: string;
  audience: AudienceType;
  audienceLabel?: string;
  sentBy: 'dj' | 'attendee';
  senderName?: string;
  senderId?: string;
  sentAt: Date;
  read: boolean;
  readBy: string[];
  readCount: number;
}

function docToMessage(docSnap: any): Message {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    eventId: data.eventId,
    content: data.content,
    audience: data.audience || 'all',
    audienceLabel: data.audienceLabel,
    sentBy: data.sentBy,
    senderName: data.senderName,
    senderId: data.senderId,
    sentAt: data.sentAt instanceof Timestamp ? data.sentAt.toDate() : new Date(data.sentAt),
    read: data.read || false,
    readBy: data.readBy || [],
    readCount: data.readCount || 0,
  };
}

export const AUDIENCE_OPTIONS: { id: AudienceType; label: string; description: string }[] = [
  { id: 'all', label: 'All Attendees', description: 'Send to everyone at the event' },
  { id: 'top-voters', label: 'Top Voters', description: 'Send to your most active voters' },
  { id: 'no-requests', label: 'No Requests Yet', description: "Send to attendees who haven't requested a song" },
];

export async function getMessagesByEventId(eventId: string): Promise<Message[]> {
  const q = query(
    collection(db, 'messages'),
    where('eventId', '==', eventId),
    orderBy('sentAt', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToMessage);
}

export async function getDJMessagesForAttendees(eventId: string): Promise<Message[]> {
  const q = query(
    collection(db, 'messages'),
    where('eventId', '==', eventId),
    orderBy('sentAt', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(docToMessage)
    .filter(m => m.sentBy === 'dj' && m.audience === 'all');
}

export async function sendDJMessage(
  eventId: string,
  content: string,
  audience: AudienceType = 'all',
  audienceLabel?: string
): Promise<Message> {
  const audienceOption = AUDIENCE_OPTIONS.find(a => a.id === audience);
  const docRef = await addDoc(collection(db, 'messages'), {
    eventId,
    content,
    audience,
    audienceLabel: audienceLabel || audienceOption?.label || 'All Attendees',
    sentBy: 'dj',
    senderName: 'DJ',
    sentAt: serverTimestamp(),
    read: false,
    readBy: [],
    readCount: 0,
  });

  return {
    id: docRef.id,
    eventId,
    content,
    audience,
    audienceLabel: audienceLabel || audienceOption?.label || 'All Attendees',
    sentBy: 'dj',
    senderName: 'DJ',
    sentAt: new Date(),
    read: false,
    readBy: [],
    readCount: 0,
  };
}

export async function sendAttendeeMessage(
  eventId: string,
  content: string,
  attendeeId: string,
  attendeeName?: string
): Promise<Message> {
  const docRef = await addDoc(collection(db, 'messages'), {
    eventId,
    content,
    audience: 'specific',
    sentBy: 'attendee',
    senderId: attendeeId,
    senderName: attendeeName || 'Attendee',
    sentAt: serverTimestamp(),
    read: false,
    readBy: [],
    readCount: 0,
  });

  return {
    id: docRef.id,
    eventId,
    content,
    audience: 'specific',
    sentBy: 'attendee',
    senderId: attendeeId,
    senderName: attendeeName || 'Attendee',
    sentAt: new Date(),
    read: false,
    readBy: [],
    readCount: 0,
  };
}

export async function markMessageAsRead(messageId: string): Promise<void> {
  await updateDoc(doc(db, 'messages', messageId), { read: true });
}

export async function markMessageReadByAttendee(messageId: string, attendeeId: string): Promise<void> {
  // Check if already read
  const q = query(
    collection(db, 'messageReads'),
    where('messageId', '==', messageId),
    where('attendeeId', '==', attendeeId)
  );
  const existing = await getDocs(q);
  if (!existing.empty) return;

  // Add read receipt
  await addDoc(collection(db, 'messageReads'), {
    messageId,
    attendeeId,
    readAt: serverTimestamp(),
  });

  // Update message read stats - add attendeeId to readBy array and increment readCount
  const msgRef = doc(db, 'messages', messageId);
  // Use a simple approach: read, check, update
  const { getDoc: getDocFn, arrayUnion } = await import('firebase/firestore');
  const msgSnap = await getDocFn(msgRef);
  if (msgSnap.exists()) {
    const data = msgSnap.data();
    const readBy: string[] = data.readBy || [];
    if (!readBy.includes(attendeeId)) {
      await updateDoc(msgRef, {
        readBy: arrayUnion(attendeeId),
        readCount: increment(1),
      });
    }
  }
}

export async function markAllMessagesReadByAttendee(eventId: string, attendeeId: string): Promise<void> {
  const messages = await getDJMessagesForAttendees(eventId);
  for (const msg of messages) {
    await markMessageReadByAttendee(msg.id, attendeeId);
  }
}

export async function getMessageReadStats(messageId: string): Promise<{ readCount: number; readBy: string[] }> {
  const { getDoc: getDocFn } = await import('firebase/firestore');
  const msgSnap = await getDocFn(doc(db, 'messages', messageId));
  if (!msgSnap.exists()) return { readCount: 0, readBy: [] };
  const data = msgSnap.data();
  return { readCount: data.readCount || 0, readBy: data.readBy || [] };
}

export async function getEstimatedAttendeeCount(eventId: string): Promise<number> {
  const messages = await getMessagesByEventId(eventId);
  const uniqueAttendees = new Set<string>();
  messages.forEach(msg => msg.readBy.forEach(id => uniqueAttendees.add(id)));
  return Math.max(uniqueAttendees.size, 1);
}

export function getMessageReadPercentage(_messageId: string, totalAttendees: number): number {
  // This is called synchronously in the UI — we return 0 and let the caller use getMessageReadStats
  if (totalAttendees <= 0) return 0;
  return 0;
}

export async function getUnreadCountForAttendee(eventId: string, attendeeId: string): Promise<number> {
  const messages = await getDJMessagesForAttendees(eventId);
  let unread = 0;
  for (const msg of messages) {
    if (!msg.readBy.includes(attendeeId)) {
      unread++;
    }
  }
  return unread;
}

export async function clearEventMessages(eventId: string): Promise<void> {
  const q = query(collection(db, 'messages'), where('eventId', '==', eventId));
  const snapshot = await getDocs(q);
  const batch = writeBatch(db);
  snapshot.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
}
